import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, Clock, Users, CheckCircle, Loader2 } from 'lucide-react';
import { SEO } from '../components/SEO';

export default function BookingPage() {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  
  const BOOKINGS_URL = 'https://outlook.office.com/book/OnlineMeeting@e-matrix.at/?ismsaljsauthenabled';

  useEffect(() => {
    // Set a timeout to hide loading state after iframe loads
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const content = {
    de: {
      title: 'Termin buchen',
      subtitle: 'Vereinbaren Sie einen Online-Termin mit unserem Team',
      loading: 'Kalender wird geladen...',
      features: [
        { icon: Calendar, title: 'Flexible Termine', desc: 'Wählen Sie einen passenden Zeitpunkt' },
        { icon: Clock, title: 'Schnelle Buchung', desc: 'In wenigen Klicks zum Termin' },
        { icon: Users, title: 'Persönliche Beratung', desc: 'Direkter Kontakt mit Experten' },
        { icon: CheckCircle, title: 'Sofortige Bestätigung', desc: 'Automatische E-Mail-Bestätigung' }
      ],
      tip: 'Tipp: Sie erhalten nach der Buchung eine automatische Bestätigung per E-Mail mit allen Details und dem Link zum Online-Meeting.'
    },
    en: {
      title: 'Book Appointment',
      subtitle: 'Schedule an online meeting with our team',
      loading: 'Loading calendar...',
      features: [
        { icon: Calendar, title: 'Flexible Scheduling', desc: 'Choose a convenient time' },
        { icon: Clock, title: 'Quick Booking', desc: 'Book in just a few clicks' },
        { icon: Users, title: 'Personal Consultation', desc: 'Direct contact with experts' },
        { icon: CheckCircle, title: 'Instant Confirmation', desc: 'Automatic email confirmation' }
      ],
      tip: 'Tip: After booking, you will receive an automatic confirmation email with all details and the link to the online meeting.'
    }
  };

  const t = content[language] || content.de;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="booking-page">
      <SEO
        title={language === 'de' ? 'Termin buchen' : 'Book Appointment'}
        description={language === 'de' ? 'Vereinbaren Sie einen Online-Termin mit dem e-Matrix Team für eine persönliche Beratung.' : 'Schedule an online meeting with the e-Matrix team for a personal consultation.'}
        path="/booking"
        lang={language}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header Section */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0D2440] mb-4">
            {t.title}
          </h1>
          <p className="text-xl text-[#5A7A9B] max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {t.features.map((feature) => (
            <div 
              key={feature.title}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow"
            >
              <feature.icon className="w-8 h-8 text-[#2E5E99] mx-auto mb-3" />
              <h3 className="font-semibold text-[#0D2440] text-sm mb-1">{feature.title}</h3>
              <p className="text-xs text-[#5A7A9B]">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* Booking Calendar Embed */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-20 bg-gray-50">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-[#2E5E99] animate-spin mx-auto mb-4" />
                <p className="text-[#5A7A9B]">{t.loading}</p>
              </div>
            </div>
          )}

          {/* Microsoft Bookings iFrame */}
          <div 
            className="relative w-full"
            style={{ 
              minHeight: '700px',
              display: isLoading ? 'none' : 'block'
            }}
          >
            <iframe
              id="bookings-iframe"
              src={BOOKINGS_URL}
              width="100%"
              height="700"
              frameBorder="0"
              scrolling="yes"
              onLoad={handleIframeLoad}
              title="Microsoft Bookings - e-Matrix"
              style={{
                border: 'none',
                width: '100%',
                minHeight: '700px'
              }}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation allow-top-navigation"
            />
          </div>
        </div>

        {/* Tip Box */}
        <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-[#2E5E99]" />
            </div>
            <p className="text-[#0D2440]">
              <strong className="text-[#2E5E99]">{language === 'de' ? 'Tipp:' : 'Tip:'}</strong>{' '}
              {t.tip.replace('Tipp: ', '').replace('Tip: ', '')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
